import React from 'react';
import { cssMap, cx } from '@atlaskit/css';
import { Box, Flex, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import Heading from '@atlaskit/heading/heading';
import ArrowRightIcon from '@atlaskit/icon/core/arrow-right';
import { CatalogIcon } from './CatalogIcon';
import { AssistantIcon } from './AssistantIcon';
import { LabsIcon } from './LabsIcon';
import { VisualiserIcon } from './VisualiserIcon';
import { ExplorerIcon } from './ExplorerIcon';
import LinkButton from '@atlaskit/button/link';

const styles = cssMap({
	container: {
		borderRadius: token('radius.large'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		width: '100%',
		textAlign: 'left',
		overflow: 'hidden',
	},
	cover: {
		paddingBlock: token('space.400'),
		height: '116px',
		backgroundColor: token('color.background.accent.blue.subtler'),
		textAlign: 'center',
	},
	content: {
		paddingInline: token('space.150'),
		paddingBlock: token('space.150'),
	},
});

const data: {
	catalog: {
		title: string;
		description: string;
		icon: ({ width, height }: { width?: number; height?: number }) => React.JSX.Element;
		link: string;
	};
	assistant: {
		title: string;
		description: string;
		icon: ({ width, height }: { width?: number; height?: number }) => React.JSX.Element;
		link: string;
	};
	labs: {
		title: string;
		description: string;
		icon: ({ width, height }: { width?: number; height?: number }) => React.JSX.Element;
		link: string;
	};
	visualiser: {
		title: string;
		description: string;
		icon: ({ width, height }: { width?: number; height?: number }) => React.JSX.Element;
		link: string;
	};
	explorer: {
		title: string;
		description: string;
		icon: ({ width, height }: { width?: number; height?: number }) => React.JSX.Element;
		link: string;
	};
} = {
	catalog: {
		title: 'Catalog',
		description: 'Explore all the entities and relationships in the graph',
		icon: CatalogIcon,
		link: '/catalog',
	},
	assistant: {
		title: 'Assistant',
		description: 'Chat with our assistant to learn about different aspects of the graph',
		icon: AssistantIcon,
		link: '/assistant',
	},
	labs: {
		title: 'Labs',
		description: 'Create and test your own cypher queries against the graph',
		icon: LabsIcon,
		link: '/labs',
	},
	visualiser: {
		title: 'Visualiser',
		description: 'Visualise entities and relationships in the graph',
		icon: VisualiserIcon,
		link: '/visualiser',
	},
	explorer: {
		title: 'Graph Explorer',
		description: 'Explore the graph in a visual way',
		icon: ExplorerIcon,
		link: '/graph-explorer',
	},
};

// Temp Props. Will updated later when we have more concrete data about cards
type CardProps = {
	type: keyof typeof data;
};

export const Card = ({ type }: CardProps): React.JSX.Element => {
	const { title, description, icon: Icon, link } = data[type];

	return (
		<Box xcss={styles.container}>
			<Box xcss={cx(styles.cover)}>
				<Icon width={60} height={60} />
			</Box>
			<Flex xcss={styles.content} gap="space.100" direction="column">
				<Box>
					<Heading size="small">{title}</Heading>
					<Text size="small" color="color.text.subtlest" maxLines={1}>
						{description}
					</Text>
				</Box>
				<Flex justifyContent="space-between">
					<LinkButton href={link} iconAfter={() => <ArrowRightIcon label="" />}>
						Explore
					</LinkButton>
				</Flex>
			</Flex>
		</Box>
	);
};
